import {
  ComponentDetails,
  DependencyWheelProps,
  ReferenceDetailsWithPositionPoints,
} from '../types';
import { Wheel } from './Wheel';
import { ComponentIconCircles } from './ComponentIconCircle';
import { AbsolutePositionedComponents } from './Component';
import { DependencyWheelContainer, Svg } from '../atoms';
import { ReferencesAsLines } from './Reference';
import { ArdoqId } from '@ardoq/api-types';
import { uniq } from 'lodash';
import { getRectangleCenter, PointAndAngle } from '@ardoq/graph';
import { calculateReferencesPathPoints } from '../modelUtils';
import { CIRCLE_RADIUS_PORTION } from '../consts';
import { getAngle } from '../utils/calculatePosition';

const shouldReferenceBeDisplayed = (
  { sourceId, targetId }: ReferenceDetailsWithPositionPoints,
  highlightedComponentId: ArdoqId | null
) => {
  return (
    !highlightedComponentId ||
    sourceId === highlightedComponentId ||
    targetId === highlightedComponentId
  );
};

export const DependencyWheel = ({
  components,
  references,
  componentIdInContext,
  highlightedComponentId,
  highlightedReferenceId,
  highlightedBy,
  onHighlight,
  onCancelHighlight,
  setComponentInContext,
  componentContextMenuAttr,
  referenceContextMenuAttr,
  onSetReferenceInContextPath,
  onReferenceClick,
  viewBounds,
}: DependencyWheelProps) => {
  if (viewBounds === null) {
    return <DependencyWheelContainer />;
  }
  const referencesWithPathPoints = calculateReferencesPathPoints(
    references,
    components,
    viewBounds
  );
  const allReferences = referencesWithPathPoints.map(reference => ({
    ...reference,
    isHighlighted: highlightedReferenceId === reference.referenceId,
    isCurrentlyDisplayed: shouldReferenceBeDisplayed(
      reference,
      highlightedComponentId
    ),
  }));

  const componentsWithReferencesToHighlightedComponent = uniq(
    allReferences
      .filter(reference => reference.isCurrentlyDisplayed)
      .flatMap(({ targetId, sourceId }) => [targetId, sourceId])
  );

  const [cx, cy] = getRectangleCenter(viewBounds);
  const [, , viewWidth, viewHeight] = viewBounds;
  const circleRadius = Math.min(viewWidth, viewHeight) * CIRCLE_RADIUS_PORTION;
  const componentPositions = new Map(
    components.map<[ComponentDetails, PointAndAngle]>((component, ii) => {
      const angle = getAngle(ii, components.length);
      return [
        component,
        [
          cx + Math.cos(angle) * circleRadius,
          cy + Math.sin(angle) * circleRadius,
          angle,
        ],
      ];
    })
  );
  return (
    <DependencyWheelContainer onClick={onCancelHighlight}>
      <Svg>
        <g data-testid="dependency-wheel-canvas">
          <Wheel viewBounds={viewBounds} />
          <ComponentIconCircles
            componentIdInContext={componentIdInContext}
            highlightedComponentId={highlightedComponentId}
            componentsWithReferencesToHighlightedComponent={
              componentsWithReferencesToHighlightedComponent
            }
            components={components}
            componentPositions={componentPositions}
          />
          <ReferencesAsLines
            references={allReferences}
            referenceContextMenuAttr={referenceContextMenuAttr}
            onSetReferenceInContextPath={onSetReferenceInContextPath}
            onReferenceClick={onReferenceClick}
            viewBounds={viewBounds}
          />
        </g>
      </Svg>
      <AbsolutePositionedComponents
        setComponentInContext={setComponentInContext}
        highlightedComponentId={highlightedComponentId}
        highlightedBy={highlightedBy}
        componentsWithReferencesToHighlightedComponent={
          componentsWithReferencesToHighlightedComponent
        }
        onHighlight={onHighlight}
        components={components}
        componentContextMenuAttr={componentContextMenuAttr}
        viewBounds={viewBounds}
        componentPositions={componentPositions}
      />
    </DependencyWheelContainer>
  );
};
